import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useCallback, useRef, useState } from 'react';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { Button } from '@/app/components/ui/button';
import { useClickOutside, useIsScreen } from '@/app/hooks';
import { cn } from '@/app/utils';

const TRY_SAMPLES_BUTTON_TEST_ID = 'try-samples-button';

interface ExpandableTrySamplesButtonProps {
  content: ReactNode;
  contentClassName?: string;
}

export const ExpandableTrySamplesButton: FC<ExpandableTrySamplesButtonProps> = ({ content, contentClassName }) => {
  const t = useTranslations();
  const [showSamples, setShowSamples] = useState(false);
  const { isLgScreen } = useIsScreen();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClickOutside = useCallback(() => {
    if (showSamples) {
      setShowSamples(false);
    }
  }, [showSamples]);

  useClickOutside(buttonRef, handleClickOutside);

  return (
    <div className={cn('w-full flex-1', !isLgScreen && showSamples ? 'overflow-hidden' : 'overflow-visible')}>
      <Button
        ref={buttonRef}
        className={cn(
          'group relative flex h-full max-w-full shrink-0 items-center justify-center px-3 font-normal text-white text-xs hover:bg-system-black24 lg:px-2',
          !isLgScreen && showSamples
            ? 'cursor-auto gap-3 rounded-10 bg-system-black24'
            : 'gap-2 rounded-[100px] lg:gap-1',
        )}
        aria-expanded={showSamples}
        data-testid={TRY_SAMPLES_BUTTON_TEST_ID}
        onClick={() => setShowSamples(true)}
      >
        <span className={cn('flex-shrink-0 opacity-80', !showSamples && 'group-hover:opacity-100')}>
          {t('create.char2VideoForm.inspire')}
        </span>

        {showSamples && (
          <div
            className={cn(
              'hide-scrollbar flex-1 overflow-x-auto lg:absolute lg:top-[-100%] lg:right-0 lg:rounded-12 lg:bg-[#3e4451] lg:p-1',
              contentClassName,
            )}
          >
            {content}
          </div>
        )}

        <BaseLineRight className={cn('shrink-0 text-base opacity-[.64]', showSamples && 'hidden lg:block')} />
      </Button>
    </div>
  );
};
